import { isPhoneAuthenticated } from '@util/phoneAuth';
import Button from '@ui/Button';
import { ProductDocument, Variation } from 'models/product';
import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeContext } from 'context/StripeContext';
import { isMobile } from '@util/index';
import { useToastContext } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { usePathname } from 'next/navigation';
import { useRouter } from '@bprogress/next';
import { hasValidVariations } from '@util/firestore/products';
import { getOfferStepList } from '../utils';
import DesktopMakeAnOfferModal from './DesktopMakeAnOfferModal';
import MobileMakeAnOfferModal from './MobileMakeAnOfferModal';
import { NavigationBar } from './NavigationBar';

interface MakeAnOfferButtonProps {
  product: ProductDocument;
  selectedVariations: Variation[] | null;
}

export default function MakeAnOfferButton({
  product,
  selectedVariations,
}: MakeAnOfferButtonProps) {
  const { userDoc, user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { stripePromise } = useStripeContext();
  const { showErrorToast } = useToastContext();
  const router = useRouter();
  const pathname = usePathname();

  const { steps, totalSteps } = getOfferStepList(product);

  return (
    <>
      <Button
        type="outline"
        width="fluid"
        text="Make an offer"
        disabled={product.out_of_stock}
        className="!h-[6rem] !rounded-full !py-6 !text-[1.8rem] !font-semibold"
        onClick={() => {
          if (userDoc && !user?.isAnonymous) {
            if (!isPhoneAuthenticated(user, userDoc)) {
              router.push('/verify-phone');
              return;
            }
            if (hasValidVariations(product, selectedVariations)) {
              setIsModalOpen(true);
            } else {
              showErrorToast('Please select all options');
            }
          } else {
            router.push(`/login?redirect=${encodeURI(pathname ?? '')}`);
          }
        }}
      />

      <Elements stripe={stripePromise}>
        {isMobile() ? (
          <MobileMakeAnOfferModal
            isOpen={isModalOpen}
            dismiss={() => setIsModalOpen(false)}
            product={product}
            selectedVariations={selectedVariations}
            stepData={{ steps, totalSteps }}
            navigationBar={
              <NavigationBar stepCount={totalSteps} currentStep={activeStep} />
            }
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        ) : (
          <DesktopMakeAnOfferModal
            isOpen={isModalOpen}
            dismiss={() => setIsModalOpen(false)}
            product={product}
            selectedVariations={selectedVariations}
            stepData={{ steps, totalSteps }}
            navigationBar={
              <NavigationBar stepCount={totalSteps} currentStep={activeStep} />
            }
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        )}
      </Elements>
    </>
  );
}
